import { useEffect, useRef, useState } from 'react';
import { ChevronDown, Keyboard, Maximize2, Minimize2 } from 'lucide-react';

import { ViewRenderer } from './renderers';
import { ComplexityBadge, VariantStrip } from './VariantStrip';
import { PseudocodePanel, StatePanel } from './PseudocodePanel';
import { NarrationBar, TransportBar } from './StageFooter';
import { CANVAS, MONO } from './theme';
import { usePlayer } from './usePlayer';
import type { TopicVisualization } from './types';

/**
 * The stage layout switches at ~760px of the PLAYER'S OWN width, not the
 * browser window's — on a phone, or embedded in a narrower container like
 * the trainer editor's live-preview side panel, the pseudocode and state
 * panels become disclosures instead of a side rail. Measuring
 * `window.innerWidth` (the original approach) got this wrong for any
 * non-full-width embed: a 520px preview panel on a 1512px desktop window
 * still measured as "wide", forcing the two-column layout into a sliver so
 * narrow the canvas rendered almost invisibly small. Rendering both layout
 * variants and hiding one with CSS would duplicate the aria-live narration
 * region, so the breakpoint has to be known in JS either way.
 */
function useIsNarrow(ref: React.RefObject<HTMLElement | null>) {
  const [narrow, setNarrow] = useState(true);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => setNarrow(el.getBoundingClientRect().width < 760);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);
  return narrow;
}

/**
 * Fullscreen state is read from `document.fullscreenElement` rather than
 * tracked locally, so it stays correct when the browser exits fullscreen on
 * its own (Esc key) without going through our toggle button.
 */
function useFullscreen(ref: React.RefObject<HTMLElement | null>) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  useEffect(() => {
    const handler = () => setIsFullscreen(document.fullscreenElement === ref.current);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, [ref]);
  const toggle = () => {
    if (document.fullscreenElement === ref.current) document.exitFullscreen();
    else ref.current?.requestFullscreen();
  };
  return { isFullscreen, toggle };
}

export function Player({
  visualization,
  batchId,
  topicId,
  focusStep,
}: {
  visualization: TopicVisualization;
  batchId?: string;
  topicId?: string;
  /** Editor-only: jumps the transport to this variant+step whenever it changes, so the preview follows whatever the trainer is currently editing instead of sitting on step 1. Never set on the real student-facing lesson page. */
  focusStep?: { variantIndex: number; stepIndex: number } | null;
}) {
  const { script } = visualization;
  const player = usePlayer(script, { skipAutoRewind: focusStep != null });
  const stageRef = useRef<HTMLDivElement>(null);
  const narrow = useIsNarrow(stageRef);
  const { isFullscreen, toggle: toggleFullscreen } = useFullscreen(stageRef);

  const { entry, variant, timeline, seek } = player;

  useEffect(() => {
    if (!focusStep) return;
    const targetIndex = timeline.findIndex(
      (e) => e.variantIndex === focusStep.variantIndex && e.stepInVariant - 1 === focusStep.stepIndex
    );
    if (targetIndex >= 0) seek(targetIndex);
    // Only re-seek when the EDITOR's focus actually moves — `timeline`/`seek`
    // are recreated on every script edit, and re-running this on every
    // keystroke would fight a trainer manually scrubbing the preview.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusStep]);

  // Keyboard control is scoped to the focused stage rather than the document:
  // Space and the arrow keys are needed by the rest of the lesson page.
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowRight') { e.preventDefault(); player.next(); }
    else if (e.key === 'ArrowLeft') { e.preventDefault(); player.prev(); }
    else if (e.key === ' ' || e.key === 'Spacebar') { e.preventDefault(); player.toggle(); }
    else if (e.key === 'Home') { e.preventDefault(); player.restart(); }
  };

  if (!entry || !variant) return null;

  const panels = (
    <>
      <PseudocodePanel
        title={variant.label}
        note={variant.note}
        lines={variant.pseudocode}
        activeLine={entry.step.line}
        problemSlug={visualization.problem_slug}
        batchId={batchId}
        topicId={topicId}
      />
      <StatePanel state={entry.step.state} />
    </>
  );

  return (
    <section
      ref={stageRef}
      tabIndex={0}
      onKeyDown={onKeyDown}
      aria-label="Algorithm visualizer"
      className={`overflow-hidden rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ff5a1f] ${isFullscreen ? 'flex h-full flex-col overflow-y-auto' : ''}`}
      style={{ backgroundColor: CANVAS.bg }}
    >
      <div className={`space-y-4 p-4 sm:p-5 ${isFullscreen ? 'flex flex-1 flex-col' : ''}`}>
        {/* Header: approaches on the left, the current approach's cost + fullscreen toggle on the right. */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <VariantStrip
            labels={script.variants.map((v) => v.label)}
            lengths={player.variantLengths}
            activeVariant={entry.variantIndex}
            stepInVariant={entry.stepInVariant}
            onSeekVariant={player.seekVariant}
          />
          <div className="flex min-w-0 items-start gap-3">
            <div className="flex min-w-0 flex-col items-start gap-1 sm:items-end">
              <ComplexityBadge
                label={variant.label}
                time={variant.complexity_time}
                space={variant.complexity_space}
              />
              <span className="text-[12px]" style={{ color: CANVAS.textFaint, fontFamily: MONO }}>
                step {player.index + 1} / {player.timeline.length}
              </span>
            </div>
            <button
              type="button"
              onClick={toggleFullscreen}
              aria-label={isFullscreen ? 'Exit fullscreen' : 'View fullscreen'}
              title={isFullscreen ? 'Exit fullscreen' : 'View fullscreen'}
              className="flex size-8 shrink-0 items-center justify-center rounded-lg border transition-colors hover:bg-white/5"
              style={{ borderColor: CANVAS.panelBorder, color: CANVAS.textFaint }}
            >
              {isFullscreen ? <Minimize2 className="size-4" /> : <Maximize2 className="size-4" />}
            </button>
          </div>
        </div>

        <div className={`${narrow ? 'space-y-4' : 'grid grid-cols-[minmax(0,1fr)_360px] items-start gap-4'} ${isFullscreen ? 'flex-1' : ''}`}>
          {/* Canvas. Wide structures scroll horizontally rather than shrinking to
              illegibility on a phone. Fullscreen gets a much taller stage since the
              whole viewport is now available instead of the card's cramped height. */}
          <div className="min-w-0 overflow-x-auto">
            <div className={`flex flex-col justify-center gap-6 py-2 ${isFullscreen ? 'min-h-[60vh]' : 'min-h-[220px] sm:min-h-[300px]'}`}>
              {/* Captions are drawn by the renderers themselves (they double as
                  the SVG accessible name), so the stage must not repeat them. */}
              {entry.step.views.map((view, i) => (
                <div key={i} className="min-w-fit">
                  <ViewRenderer view={view} />
                </div>
              ))}
            </div>
          </div>

          {narrow ? (
            <details className="group rounded-2xl border" style={{ borderColor: CANVAS.panelBorder }}>
              <summary
                className="flex cursor-pointer items-center justify-between gap-2 px-4 py-3 text-[14px] font-semibold"
                style={{ color: CANVAS.text }}
              >
                Pseudocode &amp; state
                <ChevronDown className="size-4 transition-transform group-open:rotate-180" />
              </summary>
              <div className="space-y-3 p-3 pt-0">{panels}</div>
            </details>
          ) : (
            <div className="space-y-4">{panels}</div>
          )}
        </div>

        <NarrationBar narration={entry.step.narration} line={entry.step.line} />

        <TransportBar
          index={player.index}
          total={player.timeline.length}
          playing={player.playing}
          speed={player.speed}
          atStart={player.atStart}
          atEnd={player.atEnd}
          onToggle={player.toggle}
          onNext={player.next}
          onPrev={player.prev}
          onRestart={player.restart}
          onSeek={player.seek}
          onSpeed={player.setSpeed}
        />

        <p
          className="hidden items-center gap-1.5 text-[11px] lg:flex"
          style={{ color: CANVAS.textFaint }}
        >
          <Keyboard className="size-3.5" />
          Click the stage, then use ← → to step and Space to play
        </p>
      </div>
    </section>
  );
}
